/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';
import BulkUploadModal from '../components/BulkUploadModal'; // Import the modal component

function Contact() {
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [accounts, setAccounts] = useState([]); // For account dropdown in filters
    const [loading, setLoading] = useState(true);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false); // Modal state

    // React Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Advanced filters
    const [filters, setFilters] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        designation: '',
        account_id: ''
    });

    // Filter contacts based on search term
    const filteredContacts = contacts.filter(contact => 
        contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.alternate_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.whatsapp_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.account?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply advanced filters if any are set
    const advancedFilteredContacts = filteredContacts.filter(contact => {
        return (
            (filters.first_name === '' ||
                contact.first_name?.toLowerCase().includes(filters.first_name.toLowerCase())) &&
            (filters.last_name === '' ||
                contact.last_name?.toLowerCase().includes(filters.last_name.toLowerCase())) &&
            (filters.email === '' ||
                contact.email?.toLowerCase().includes(filters.email.toLowerCase())) &&
            (filters.phone === '' ||
                contact.phone?.includes(filters.phone)) &&
            (filters.designation === '' ||
                contact.designation?.toLowerCase().includes(filters.designation.toLowerCase())) &&
            (filters.account_id === '' ||
                contact.account_id === filters.account_id)
        );
    });

    // Sort filtered contacts
    const sortedContacts = React.useMemo(() => {
        let sortableContacts = [...advancedFilteredContacts];
        if (sortConfig.key) {
            sortableContacts.sort((a, b) => {
                let aValue = sortConfig.key === 'account_name' ? a.account?.company_name : a[sortConfig.key];
                let bValue = sortConfig.key === 'account_name' ? b.account?.company_name : b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableContacts;
    }, [advancedFilteredContacts, sortConfig]);

    // Calculate pagination
    const totalItems = sortedContacts.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentContacts = sortedContacts.slice(startIndex, endIndex);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch Contacts from API
    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/contacts/");
            console.log("Contacts response:", response.data);
            setContacts(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching Contacts:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch contacts data'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch Accounts for filter dropdown
    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get("/accounts/");
            setAccounts(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching Accounts:", error);
        }
    };

    useEffect(() => {
        fetchContacts();
        fetchAccounts();
    }, []);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            designation: '',
            account_id: ''
        });
        setSearchTerm(''); // Also clear the main search term
    };

    // Navigate to Contact Details page
    const handleViewDetails = (contactId) => {
        navigate(`/contact-details/${contactId}`);
    };

    // Edit Contact - Navigate to add-contact page with contact data for update
    const handleEdit = (contact) => {
        console.log("contact data:", contact)
        navigate('/Add-contact', { state: { contact, isEdit: true } });
    };

    // Delete Contact
    const handleDelete = async (contactId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/contacts/${contactId}`);
                Swal.fire('Deleted!', 'Contact has been deleted.', 'success');
                fetchContacts(); // Refresh the list
            } catch (error) {
                console.error('Error deleting contact:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete contact'
                });
            }
        }
    };

    // Handle bulk upload success
    const handleBulkUploadSuccess = () => {
        fetchContacts(); // Refresh the contacts list after successful upload
    };

    // Get full name for display
    const getFullName = (contact) => {
        const firstName = contact.first_name || '';
        const lastName = contact.last_name || '';
        return `${firstName} ${lastName}`.trim() || 'N/A';
    };

    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredContacts.length !== contacts.length && ` (filtered from ${contacts.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                    </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Contacts</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button
                                            className="btn btn-filters w-auto"
                                            onClick={() => $('#filter_inputs').toggle()}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            title="Filter"
                                        >
                                            <span className="me-2">
                                                <img src="assets/img/icons/filter-icon.svg" alt="filter" />
                                            </span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-pdf me-2"></i>PDF
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-text me-2"></i>CSV
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Print">
                                            <span><i className="fe fe-printer"></i></span>
                                        </a>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-import" 
                                            onClick={() => setShowBulkUploadModal(true)}
                                        >
                                            <span><i className="fe fe-check-square me-2"></i>Import Contacts</span>
                                        </button>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="/Add-contact">
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add Contact
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Search Filter */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="first_name"
                                            value={filters.first_name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by first name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="last_name"
                                            value={filters.last_name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by last name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            placeholder="Search by email"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={filters.phone}
                                            onChange={handleFilterChange}
                                            placeholder="Search by phone"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Designation</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="designation"
                                            value={filters.designation}
                                            onChange={handleFilterChange}
                                            placeholder="Search by designation"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Account</label>
                                        <select
                                            className="form-control"
                                            name="account_id"
                                            value={filters.account_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Accounts</option>
                                            {accounts.map(account => (
                                                <option key={account.id} value={account.id}>
                                                    {account.company_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={clearFilters}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Search Filter */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search contacts..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('first_name')}
                                                    >
                                                        Name {getSortIcon('first_name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('email')}
                                                    >
                                                        Email {getSortIcon('email')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('phone')}
                                                    >
                                                        Phone {getSortIcon('phone')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('designation')}
                                                    >
                                                        Designation {getSortIcon('designation')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('account_name')}
                                                    >
                                                        Account {getSortIcon('account_name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('isactive')}
                                                    >
                                                        Status {getSortIcon('isactive')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            <div className="spinner-border" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : currentContacts.length > 0 ? (
                                                    currentContacts.map((contact, index) => (
                                                        <tr key={contact.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <span 
                                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                    onClick={() => handleViewDetails(contact.id)}
                                                                    title="Click to view details"
                                                                >
                                                                    {getFullName(contact)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {contact.email ? (
                                                                    <a href={`mailto:${contact.email}`} className="text-primary">
                                                                        {contact.email}
                                                                    </a>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                            <td>{contact.phone || 'N/A'}</td>
                                                            <td>{contact.designation || 'N/A'}</td>
                                                            <td>
                                                                {contact.account ? (
                                                                    <span 
                                                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                                                        onClick={() => navigate(`/account-details/${contact.account.id}`)}
                                                                        title="Click to view account details"
                                                                    >
                                                                        {contact.account.company_name}
                                                                    </span>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${contact.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                                    {contact.isactive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-primary-light me-2"
                                                                    onClick={() => handleViewDetails(contact.id)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-success-light me-2"
                                                                    onClick={() => handleEdit(contact)}
                                                                >
                                                                    <i className="fa fa-edit me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon btn-greys bg-danger-light"
                                                                    onClick={() => handleDelete(contact.id)}
                                                                >
                                                                    <i className="fa fa-trash-alt me-1"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            {searchTerm ? "No matching contacts found" : "No contacts available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>

            {/* Bulk Upload Modal */}
            <BulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
        </div>
    )
}

export default Contact;